import type { KatalogSprache } from '../../services/mangelKatalog/typen';

/**
 * Oberflächentexte der Katalog-Bauteile in der Katalogsprache (englische
 * Fassung, 28.09.): Torte, Kacheln, Leiste, Listen, ⓘ, Chip, Dialog. Die
 * Inhalte (Kategorien, Kurztexte, Vorlagen) kommen aus dem aufgelösten
 * Katalog; hier stehen nur die festen Beschriftungen. Keine Herkunft, keine
 * Nummer (Entscheid 28.09.).
 */
export interface KatalogTexte {
    // Leiste
    suche: string;
    zuletzt: string;
    favoriten: string;
    freitext: string;
    leisteAria: string;
    // Torte und Kacheln
    /** Blätter-Stück bzw. -Kachel (Seitenregel 8 + 1). */
    sonstiges: string;
    /** Screenreader: „Sonstiges, weitere 3“. */
    weitere: (n: string | number) => string;
    themaWaehlen: string;
    zurueck: string;
    seiteZurueck: string;
    vorigeSeite: (seite: number, von: number) => string;
    zurueckZurThemenauswahl: string;
    seite: (seite: number, von: number) => string;
    freitextIn: (thema: string) => string;
    hinweisZu: (kurz: string) => string;
    favoritEntfernen: (kurz: string) => string;
    alsFavoritMerken: (kurz: string) => string;
    // ⓘ-Einblendung
    hinweisSchliessen: string;
    vorschrift: string;
    uebernehmen: string;
    // Suche, Zuletzt, Favoriten
    suchPlatzhalter: string;
    suchAria: string;
    sucheLeer: string;
    keineTreffer: string;
    zuletztGewaehlt: string;
    nochNichtsGewaehlt: string;
    nochKeineFavoriten: string;
    gesamtkatalogLaedt: string;
    erneutVersuchen: string;
    bisDahinNurTorte: string;
    einigeEintraegeDanach: string;
    // Chip im Schritt „Details“
    standardPraefix: string;
    themaPraefix: string;
    ohneThema: string;
    aendern: string;
    standardmangelEntfernen: string;
    angabeErgaenzen: string;
    // Dialog
    dialogTitel: string;
    dialogTitelFuer: (nummer: string | number) => string;
    schliessen: string;
    // Mehrfachauswahl (Paket L, 30.09.)
    mehrfachTipp: string;
    gewaehltAnzahl: (n: number) => string;
    auswahlVerwerfen: string;
    /** Chip: „· 2 Punkte“. */
    punkte: (n: number) => string;
    hinzufuegen: string;
    ausAuswahlEntfernen: string;
}

const DE: KatalogTexte = {
    suche: 'Suche',
    zuletzt: 'Zuletzt',
    favoriten: 'Favoriten',
    freitext: 'Freitext',
    leisteAria: 'Katalog durchsuchen',
    sonstiges: 'Sonstiges',
    weitere: (n) => `weitere ${n}`,
    themaWaehlen: 'Thema wählen',
    zurueck: 'Zurück',
    seiteZurueck: 'Seite\nzurück',
    vorigeSeite: (seite, von) => `Vorige Seite (Seite ${seite} von ${von})`,
    zurueckZurThemenauswahl: 'Zurück zur Themenauswahl',
    seite: (seite, von) => `Seite ${seite}/${von}`,
    freitextIn: (thema) => `Freitext in ${thema}`,
    hinweisZu: (kurz) => `Hinweis zu ${kurz}`,
    favoritEntfernen: (kurz) => `${kurz}: Favorit entfernen`,
    alsFavoritMerken: (kurz) => `${kurz}: als Favorit merken`,
    hinweisSchliessen: 'Hinweis schließen',
    vorschrift: 'Vorschrift:',
    uebernehmen: 'Übernehmen',
    suchPlatzhalter: 'Stichwort, z. B. Bordbrett',
    suchAria: 'Im Mängelkatalog suchen',
    sucheLeer: 'Stichwort eingeben, z. B. „Bordbrett“ oder „Seitenschutz“.',
    keineTreffer: 'Keine Treffer.',
    zuletztGewaehlt: 'Zuletzt gewählt',
    nochNichtsGewaehlt: 'Noch nichts gewählt.',
    nochKeineFavoriten: 'Noch keine Favoriten. Der Stern an einer Kachel merkt einen Mangel vor.',
    gesamtkatalogLaedt: 'Gesamtkatalog lädt bei Netz',
    erneutVersuchen: 'Erneut versuchen',
    bisDahinNurTorte: 'bis dahin nur Torte und eigene Einträge.',
    einigeEintraegeDanach: 'einige Einträge erscheinen danach.',
    standardPraefix: 'Standard:',
    themaPraefix: 'Thema:',
    ohneThema: 'Ohne Thema',
    aendern: 'Ändern',
    standardmangelEntfernen: 'Standardmangel entfernen',
    angabeErgaenzen: 'Angabe ergänzen: Die Vorlage enthält eine Lücke — bitte im Text vervollständigen.',
    dialogTitel: 'Standardmangel wählen',
    dialogTitelFuer: (nummer) => `Standardmangel für #${nummer} wählen`,
    schliessen: 'Schließen',
    mehrfachTipp: 'Tipp: Kachel gedrückt halten, um mehrere Punkte zu einem Mangel zusammenzufassen.',
    gewaehltAnzahl: (n) => `${n} gewählt`,
    auswahlVerwerfen: 'Auswahl verwerfen',
    punkte: (n) => `${n} Punkte`,
    hinzufuegen: 'Hinzufügen',
    ausAuswahlEntfernen: 'Aus der Auswahl entfernen',
};

const EN: KatalogTexte = {
    suche: 'Search',
    zuletzt: 'Recent',
    favoriten: 'Favourites',
    freitext: 'Free text',
    leisteAria: 'Browse the catalogue',
    sonstiges: 'More',
    weitere: (n) => `${n} more`,
    themaWaehlen: 'Choose a topic',
    zurueck: 'Back',
    seiteZurueck: 'Page\nback',
    vorigeSeite: (seite, von) => `Previous page (page ${seite} of ${von})`,
    zurueckZurThemenauswahl: 'Back to topics',
    seite: (seite, von) => `Page ${seite}/${von}`,
    freitextIn: (thema) => `Free text in ${thema}`,
    hinweisZu: (kurz) => `Details of ${kurz}`,
    favoritEntfernen: (kurz) => `${kurz}: remove from favourites`,
    alsFavoritMerken: (kurz) => `${kurz}: add to favourites`,
    hinweisSchliessen: 'Close details',
    vorschrift: 'Regulation:',
    uebernehmen: 'Apply',
    suchPlatzhalter: 'Keyword, e.g. toe board',
    suchAria: 'Search the defect catalogue',
    sucheLeer: 'Enter a keyword, e.g. “toe board” or “edge protection”.',
    keineTreffer: 'No results.',
    zuletztGewaehlt: 'Recently chosen',
    nochNichtsGewaehlt: 'Nothing chosen yet.',
    nochKeineFavoriten: 'No favourites yet. The star on a tile saves a defect here.',
    gesamtkatalogLaedt: 'Full catalogue loads when online',
    erneutVersuchen: 'Retry',
    bisDahinNurTorte: 'until then only the pie menu and your own entries.',
    einigeEintraegeDanach: 'some entries will appear afterwards.',
    standardPraefix: 'Standard:',
    themaPraefix: 'Topic:',
    ohneThema: 'No topic',
    aendern: 'Change',
    standardmangelEntfernen: 'Remove standard defect',
    angabeErgaenzen: 'Complete the details: the template contains a gap — please fill it in.',
    dialogTitel: 'Choose a standard defect',
    dialogTitelFuer: (nummer) => `Choose a standard defect for #${nummer}`,
    schliessen: 'Close',
    mehrfachTipp: 'Tip: press and hold a tile to combine several items into one defect.',
    gewaehltAnzahl: (n) => `${n} selected`,
    auswahlVerwerfen: 'Discard selection',
    punkte: (n) => `${n} items`,
    hinzufuegen: 'Add',
    ausAuswahlEntfernen: 'Remove from selection',
};

export const KATALOG_TEXTE: Readonly<Record<KatalogSprache, KatalogTexte>> = Object.freeze({ de: DE, en: EN });

/** Texte der Sprache; ohne (gültige) Angabe deutsch. */
export const katalogTexte = (sprache?: KatalogSprache | null): KatalogTexte => KATALOG_TEXTE[sprache === 'en' ? 'en' : 'de'];
